/**
 * Editable Address Directive
 * Adds Google Places Autocomplete to x-editable address fields
 * 
 * Usage:
 *   <a href="#" 
 *      editable-text="model.address" 
 *      editable-address
 *      e-placeholder="Start typing address..."
 *      e-options="{types: ['address'], componentRestrictions: {country: 'us'}}"
 *      buttons="no" 
 *      blur="submit" 
 *      onaftersave="saveCallback($data)">
 *     {{model.address || 'Click to add address'}}
 *   </a>
 */
function editableAddressDirective($window, $timeout) {
  'ngInject';

  return {
    restrict: 'A',
    link: function(scope, element, attrs) {
      let autocomplete = null;
      let inputElement = null;
      let checkInterval = null;

      // Function to initialize autocomplete on the input
      function initializeAutocomplete() {
        if (!inputElement || autocomplete) {
          return;
        }

        if ($window.google && $window.google.maps && $window.google.maps.places) {
          // Get options from attributes
          let options = {
            types: ['address'],
            componentRestrictions: null
          };

          if (attrs.eOptions) {
            try {
              const parsedOptions = scope.$eval(attrs.eOptions);
              if (parsedOptions) {
                options = angular.extend(options, parsedOptions);
              }
            } catch (e) {
              console.warn('Error parsing e-options:', e);
            }
          }

          const autocompleteOptions = {
            types: options.types || ['address'],
            componentRestrictions: options.componentRestrictions || null,
          };

          // Create the autocomplete instance
          autocomplete = new $window.google.maps.places.Autocomplete(inputElement, autocompleteOptions);

          // Listen for place selection
          autocomplete.addListener('place_changed', function() {
            const place = autocomplete.getPlace();
            
            if (!place.geometry) {
              // User entered something that wasn't selected from the dropdown
              // The value is already in the input, x-editable will handle it
              return;
            }

            // Update the input with the formatted address
            const formattedAddress = place.formatted_address || inputElement.value;
            
            // Use Angular's $apply to update the scope if needed
            scope.$apply(function() {
              // Set the input value
              inputElement.value = formattedAddress;
              
              // Trigger Angular's input handler by setting the ng-model value
              // x-editable uses ng-model internally
              const ngModelCtrl = angular.element(inputElement).controller('ngModel');
              if (ngModelCtrl) {
                ngModelCtrl.$setViewValue(formattedAddress);
                ngModelCtrl.$render();
              }
            });
          });

          // Set placeholder if provided
          if (attrs.ePlaceholder) {
            inputElement.placeholder = attrs.ePlaceholder;
          } else {
            inputElement.placeholder = 'Start typing your address...';
          }
          
          // Set minimum width on the input to ensure autocomplete dropdown isn't clipped
          inputElement.style.minWidth = '400px';
          inputElement.style.width = '100%';
          
          // Ensure the parent editable form doesn't clip
          const editableForm = angular.element(inputElement).closest('.editableform');
          if (editableForm.length) {
            editableForm[0].style.overflow = 'visible';
            editableForm[0].style.position = 'relative';
            editableForm[0].style.zIndex = '1000';
            editableForm[0].style.width = '100%';
          }
          
          // Ensure the editable container doesn't clip
          const editableContainer = angular.element(inputElement).closest('.editable-container');
          if (editableContainer.length) {
            editableContainer[0].style.overflow = 'visible';
            editableContainer[0].style.position = 'relative';
            editableContainer[0].style.zIndex = '1000';
          }

          // Clear the interval since we found and initialized the input
          if (checkInterval) {
            $timeout.cancel(checkInterval);
            checkInterval = null;
          }
        } else {
          // Wait and try again
          $timeout(initializeAutocomplete, 100);
        }
      }

      // Function to find the input element created by x-editable
      function findInputElement() {
        // Reset if we had a previous autocomplete but the input is gone or different
        if (autocomplete && inputElement && !document.body.contains(inputElement)) {
          // Previous input was removed (x-editable cleaned up), reset for new input
          if ($window.google && $window.google.maps) {
            $window.google.maps.event.clearInstanceListeners(autocomplete);
          }
          autocomplete = null;
          inputElement = null;
        }
        
        if (autocomplete && inputElement && document.body.contains(inputElement)) {
          return true; // Already initialized and input still exists
        }

        // Try multiple selectors to find the input
        // x-editable creates different structures depending on the theme
        let input = null;

        // Method 1: Look for input in the closest editable form
        const editableForm = element.closest('.editableform');
        if (editableForm.length) {
          input = editableForm.find('input[type="text"]')[0];
        }

        // Method 2: Look in the parent's editable container
        if (!input) {
          const editableContainer = element.parent().find('.editable-container, .editable-input');
          if (editableContainer.length) {
            input = editableContainer.find('input[type="text"]')[0];
          }
        }

        // Method 3: Look for any input that's a sibling or in nearby DOM
        if (!input) {
          // x-editable might insert the form near the element
          const parent = element.parent();
          input = parent.find('input[type="text"].editable-input, input[type="text"].form-control')[0];
        }

        // Method 4: Search in the document for the most recent editable input
        if (!input) {
          const allEditableInputs = angular.element(document).find('.editableform input[type="text"]');
          if (allEditableInputs.length > 0) {
            // Get the last one (most recently created)
            input = allEditableInputs[allEditableInputs.length - 1];
          }
        }

        if (input && input !== inputElement) {
          inputElement = input;
          initializeAutocomplete();
          return true;
        }

        return false;
      }

      // Use MutationObserver to watch for when x-editable adds the input to DOM
      let observer = null;
      function startObserving() {
        if (observer || autocomplete || !window.MutationObserver) {
          return;
        }

        observer = new MutationObserver(function(mutations) {
          if (!autocomplete && findInputElement()) {
            // Found and initialized, can stop observing
            stopObserving();
          }
        });

        // Observe a limited scope - the element's parent and siblings
        const observeTarget = element.parent().length ? element.parent()[0] : document.body;
        observer.observe(observeTarget, {
          childList: true,
          subtree: true
        });

        // Also observe document.body as fallback (x-editable might append elsewhere)
        if (observeTarget !== document.body) {
          const bodyObserver = new MutationObserver(function(mutations) {
            if (!autocomplete && findInputElement()) {
              stopObserving();
              if (bodyObserver) {
                bodyObserver.disconnect();
              }
            }
          });
          bodyObserver.observe(document.body, {
            childList: true,
            subtree: true
          });

          // Store body observer reference for cleanup
          scope.bodyObserver = bodyObserver;
        }
      }

      function stopObserving() {
        if (observer) {
          observer.disconnect();
          observer = null;
        }
        if (scope.bodyObserver) {
          scope.bodyObserver.disconnect();
          scope.bodyObserver = null;
        }
      }

      // Watch for when x-editable shows the input (jQuery event)
      element.on('shown', function() {
        // x-editable fires 'shown' event when the form is displayed
        stopObserving(); // Stop any existing observation
        $timeout(function() {
          if (findInputElement()) {
            stopObserving();
          }
        }, 0);
        $timeout(function() {
          if (findInputElement()) {
            stopObserving();
          }
        }, 50);
        $timeout(function() {
          if (findInputElement()) {
            stopObserving();
          }
        }, 100);
      });

      // Also watch for clicks to catch when editing starts
      element.on('click', function() {
        // Start observing when user clicks to edit
        startObserving();
        
        // Start checking after a short delay to allow x-editable to create the DOM
        $timeout(function() {
          if (findInputElement()) {
            stopObserving();
          }
        }, 50);
        $timeout(function() {
          if (findInputElement()) {
            stopObserving();
          }
        }, 100);
        $timeout(function() {
          if (findInputElement()) {
            stopObserving();
          }
        }, 150);
      });

      // Periodic check as a fallback (will be cleared once input is found)
      let checkCount = 0;
      function periodicCheck() {
        if (checkCount++ > 30) {
          // Stop checking after 30 attempts (3 seconds)
          if (checkInterval) {
            $timeout.cancel(checkInterval);
            checkInterval = null;
          }
          return;
        }
        if (findInputElement()) {
          if (checkInterval) {
            $timeout.cancel(checkInterval);
            checkInterval = null;
          }
          stopObserving();
        } else if (!autocomplete) {
          checkInterval = $timeout(periodicCheck, 100);
        }
      }

      // Start periodic check when element is clicked (fallback)
      element.on('click', function() {
        if (!autocomplete && !checkInterval) {
          checkCount = 0;
          periodicCheck();
        }
      });

      // Cleanup on destroy
      scope.$on('$destroy', function() {
        if (checkInterval) {
          $timeout.cancel(checkInterval);
        }
        stopObserving();
        if (autocomplete && $window.google && $window.google.maps) {
          $window.google.maps.event.clearInstanceListeners(autocomplete);
          autocomplete = null;
        }
        element.off('shown click');
      });
    }
  };
}

export default ['$window', '$timeout', editableAddressDirective];




// WEBPACK FOOTER //
// ./client/common/directives/google-address-autocomplete/editable-address.directive.js